// src/lib/test-engine/pretest.ts
//
// Pretest / equating (unscored) item support for the SAT engine (#783).
//
// The real Digital SAT embeds a small number of indistinguishable, unscored
// "pretest" items in each module (used to calibrate future questions). They do
// not count toward the score and do not affect M1->M2 routing. We match that by
// designating N of each module's served items as unscored at assembly time —
// the served module size is unchanged (so timing + count assertions hold); a
// random subset is simply flagged scored=false.
//
// Per College Board (Summer 2022 Digital SAT spec): each RW module is 27
// questions = 25 operational + 2 pretest; each Math module is 22 = 20
// operational + 2 pretest (8 pretest across the full test).
import { shuffle } from '@/lib/sat/scoring'

/** Unscored pretest items embedded per module, by section. */
export const PRETEST_PER_MODULE: Record<'RW' | 'MATH', number> = {
  RW: 2,
  MATH: 2,
}

/**
 * Given a module's served question ids (in order) and how many should be
 * unscored pretest items, return a `scored` flag per id (aligned to `ids`).
 *
 * Exactly `pretestCount` ids are marked unscored, chosen at random so they're
 * indistinguishable and not biased toward any position or difficulty. Always
 * keeps at least one scored item (a fully-unscored section would make the
 * attempt un-scoreable), and treats a non-positive/oversized count safely.
 */
export function assignScoredFlags(ids: string[], pretestCount: number): boolean[] {
  const n = ids.length
  const flags = new Array<boolean>(n).fill(true)
  const k = Math.max(0, Math.min(pretestCount, n - 1))
  if (k === 0) return flags
  const pretestIdx = shuffle([...Array(n).keys()]).slice(0, k)
  for (const i of pretestIdx) flags[i] = false
  return flags
}
